import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../Assets/logo.png'; // Adjust path as needed
import { Avatar } from '@mui/material';
import ProfilePic from './ProfilePic'; // Adjust path as needed
import { ref, get, child } from 'firebase/database';
import { database } from '../../firebase';
import { Home, Chat, Event, Notifications, AccountCircle, ExitToApp } from '@mui/icons-material'; // Material UI Icons
 
const TopBar = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
 
  // Fetch profile data from Firebase on component mount
  useEffect(() => {
    const fetchProfileData = async () => {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));
      if (!storedUser) return;
      try {
        const dbRef = ref(database);
        const snapshot = await get(child(dbRef, 'users'));
        if (snapshot.exists()) {
          const usersData = snapshot.val();
          // Find the current user by email
          const userEntry = Object.entries(usersData).find(([key, value]) =>
            value.email &&
            value.email.toLowerCase() === storedUser.email.toLowerCase()
          );
          if (userEntry) {
            const [userKey, userProfile] = userEntry;
            setProfileData(userProfile);
          }
        }
      } catch (err) {
        console.error("Error fetching profile in TopBar:", err);
      }
    };
 
    fetchProfileData();
  }, []);
 
  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
 
  // Fallback: if profileData not fetched, try localStorage
  const storedUser = JSON.parse(localStorage.getItem("currentUser"));
  const userName = profileData
    ? `${profileData.first_name} ${profileData.last_name}`
    : storedUser
    ? `${storedUser.first_name || 'John'} ${storedUser.last_name || 'Doe'}`
    : "John Doe";
 
  const profileImage = "https://via.placeholder.com/40"; // Placeholder for profile image
 
  const navLinks = [
    { name: 'Locker', route: '/user', icon: <Home fontSize="medium" /> },
    { name: 'Chats', route: '/customer', icon: <Chat fontSize="medium" /> },
    { name: 'Activity', route: '/activity', icon: <Event fontSize="medium" /> },
    { name: 'Notifications', route: '/payment', icon: <Notifications fontSize="medium" /> },
  ];
 
  const styles = {
    topBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0 20px',
      background: 'rgb(168, 233, 243)', // Updated background color
      color: '#004c53',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      height: '60px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
    },
    logoImage: {
      height: '40px',
      width: 'auto',
    },
    nav: {
      display: isMobile ? (menuOpen ? 'flex' : 'none') : 'flex',
      flexDirection: isMobile ? 'column' : 'column',
      position: isMobile ? 'fixed' : 'static',
      top: isMobile ? '60px' : 'auto',
      left: 0,
      width: isMobile ? '100%' : 'auto',
      height: isMobile ? 'calc(100% - 60px)' : 'auto',
      backgroundColor: '#fff',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      padding: isMobile ? '40px 20px' : '0 20px',
      transition: 'all 0.3s ease',
      boxShadow: isMobile ? '0 8px 16px rgba(0, 0, 0, 0.3)' : 'none',
      zIndex: 999,
    },
    navLink: {
      margin: '10px 0',
      cursor: 'pointer',
      color: '#004c53',
      textDecoration: 'none',
      fontSize: '1.2em',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '10px',
      transition: 'color 0.3s, background-color 0.3s, transform 0.3s',
      paddingLeft: '10px',
    },
    hoverEffect: {
      backgroundColor: '#e0f7fa',
      color: '#00acc1',
      transform: 'scale(1.05)',
    },
    profileSection: {
      position: 'absolute',
      bottom: '130px', // Increased from previous value to push further down
      left: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '5px', // Reduced gap between profile and logout
    },
    profileLink: {
      cursor: 'pointer',
      color: '#004c53',
      padding: '8px 5px', // Slightly reduced padding
      fontSize: '1.2em',
      transition: 'transform 0.3s',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      paddingLeft: '10px',
    },
    logoutButton: {
      cursor: 'pointer',
      color: '#004c53',
      background: 'none',
      border: 'none',
      padding: '8px 10px', // Slightly reduced padding to match profile
      fontSize: '1.2em',
      transition: 'transform 0.3s',
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginTop: '0', // Removed marginTop since we're using gap in profileSection
    },
    hamburger: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-around',
      width: '25px',
      height: '25px',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
      zIndex: 1001,
      outline: 'none',
    },
    bar: (index) => ({
      width: '100%',
      height: '3px',
      backgroundColor: '#00acc1',
      borderRadius: '2px',
      transition: 'all 0.3s',
      transform: menuOpen
        ? index === 1
          ? 'rotate(45deg) translate(5px, 5px)'
          : index === 2
          ? 'rotate(-45deg) translate(5px, -5px)'
          : 'scale(0)'
        : 'rotate(0) scale(1)',
    }),
  };
 
  const handleLogout = () => navigate('/');
 
  return (
    <>
      <div style={styles.topBar}>
        <div style={styles.logo} onClick={() => navigate('/user')}>
          <img src={logo} alt="Logo" style={styles.logoImage} />
        </div>
 
        {!isMobile ? (
          <nav style={styles.nav}>
            {navLinks.map((link) => (
              <span
                key={link.name}
                style={styles.navLink}
                className="mobile-menu-link"
                onClick={() => navigate(link.route)}
                tabIndex="0"
                role="button"
                aria-label={link.name}
              >
                {link.icon} {link.name}
              </span>
            ))}
            <div style={styles.profileSection}>
              <span
                style={styles.profileLink}
                onClick={() => setProfileModalOpen(true)}
              >
                <AccountCircle fontSize="medium" /> Profile
              </span>
              <button
                onClick={handleLogout}
                style={styles.logoutButton}
              >
                <ExitToApp fontSize="medium" /> Logout
              </button>
            </div>
          </nav>
        ) : (
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={styles.hamburger}
            aria-label="Toggle menu"
          >
            {[0, 1, 2].map((i) => (
              <div key={i} style={styles.bar(i)}></div>
            ))}
          </button>
        )}
      </div>
 
      {isMobile && menuOpen && (
        <div
          style={styles.overlay}
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        ></div>
      )}
 
      {isMobile && (
        <nav style={styles.nav}>
          {navLinks.map((link) => (
            <span
              key={link.name}
              style={styles.navLink}
              onClick={() => {
                navigate(link.route);
                setMenuOpen(false);
              }}
            >
              {link.icon} {link.name}
            </span>
          ))}
          <div style={styles.profileSection}>
            <span
              style={styles.profileLink}
              onClick={() => setProfileModalOpen(true)}
            >
              <AccountCircle fontSize="medium" /> Profile
            </span>
            <button
              onClick={handleLogout}
              style={styles.logoutButton}
            >
              <ExitToApp fontSize="medium" /> Logout
            </button>
          </div>
        </nav>
      )}
 
      <ProfilePic
        open={profileModalOpen}
        handleClose={() => setProfileModalOpen(false)}
      />
    </>
  );
};
 
export default TopBar;  